<template>
  <v-expand-transition>
    <v-card
      v-show="showDetail"
      flat
      style="margin-top: 20px"
    >
      <div class="pt-20 pb-20">
        <div class="wo-inner-container">
          <div class="wo-line" />
          <WorkOrderSection
            iconName="mdi-cog"
            :showReportSection="false"
            :displayShowHideSection="false"
          >
            <template #child>
              <v-row
                v-if="!workOrderGraph"
                style="padding: 10px"
                class="align-center justify-space-between"
              >
                <v-col class="p-0">
                  <h3 style="font-size: 20px; font-weight: 500">
                    {{ $t('administration.configuration') }}
                  </h3>
                </v-col>
                <v-col
                  class="p-0 d-flex justify-end"
                >
                  <v-switch
                    v-model="displayInDashboard"
                    style="margin-top: 0px; padding-top: 0px"
                    :label="$t('graphics.displayInDashboard')"
                    hideDetails
                    @change="onSettingChange('displayInDashboard')"
                  />
                </v-col>
              </v-row>
              <v-row style="padding: 10px 0px 10px 20px;">
                <GraphicDateRange
                  :graphicId="graphicId"
                  :graphic="graphic"
                  @reload="$emit('reload')"
                  @dateRangeChange="updateOneSeries"
                />
              </v-row>
              <v-row
                v-if="!workOrderGraph"
                style="padding: 10px 5px 20px 10px"
              >
                <v-col class="ml-3">
                  <span
                    class="font-weight-bold"
                    style="font-size: 16px;"
                  >{{ $t('graphics.data') }}</span>
                </v-col>
                <v-col
                  v-if="series.length < 4"
                  cols="auto"
                  class="p-0"
                >
                  <v-btn
                    elevation="1"
                    class="white--text btn-chart"
                    color="#26384B"
                    :disabled="addSeriesDisabled"
                    @click="addSeries"
                  >
                    {{ $t('administration.add') }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row v-if="!workOrderGraph">
                <Series
                  :series="series"
                  :graphic="graphic"
                  @getSeries="fetchSeries"
                  @updateOneSeries="updateOneSeries"
                  @getSeriesData="getSeriesData"
                />
              </v-row>
            </template>
          </WorkOrderSection>
          <WorkOrderSection
            iconName="bar_chart"
            :showReportSection="false"
            :displayShowHideSection="false"
            :isLast="workOrderGraph"
          >
            <template #child>
              <v-row>
                <div style="width:100%; height: 100%;">
                  <D3LineChart
                    ref="d3LineChart"
                    :series="series"
                    :seriesData="mutatedSeriesData"
                    :graphic="graphic"
                    :showSpinner="showSpinner"
                    :showSpinnerOnUpdate="showSpinnerOnUpdate"
                    :workOrderGraph="workOrderGraph"
                    @updatedOpenedGraphs="updatedOpenedGraphs"
                  />
                </div>
              </v-row>
            </template>
          </WorkOrderSection>
          <WorkOrderSection
            iconName="rate_review"
            :showSection="!workOrderGraph"
            :showReportSection="!workOrderGraph"
            :displayShowHideSection="false"
            isLast
          >
            <template #child>
              <GraphicComment :graphicId="graphicId" />
            </template>
          </WorkOrderSection>
        </div>
      </div>
    </v-card>
  </v-expand-transition>
</template>
<script>
import { mapGetters } from 'vuex';
import Series from '@/components/graphics/Series.vue';
import GraphicComment from '@/components/graphics/GraphicComment.vue';
import GraphicDateRange from '@/components/graphics/GraphicDateRange.vue';
import WorkOrderSection from '@/views/workOrders/overview/WorkOrderSection';
import D3LineChart from '@/components/graphics/D3LineChart.vue';

export default {
    components: {
        Series,
        GraphicComment,
        GraphicDateRange,
        WorkOrderSection,
        D3LineChart,
    },
    props: {
        graphic: {
            type: Object,
            default: () => ({}),
        },
        showDetail: {
            type: Boolean,
            default: false,
        },
        series: {
            type: Array,
            default: () => [],
        },
        seriesData: {
            type: Array,
            default: () => [],
        },
        workOrderGraph: {
            type: Boolean,
            default: false,
        },
        showSpinner: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            displayInDashboard: false,
            addSeriesDisabled: false,
            mutatedSeriesData: [],
            showSpinnerOnUpdate: false,
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'teamMainLang',
            'getSeries',
        ]),
        graphicId() {
            return this.graphic.id;
        },
    },
    watch: {
        graphic: {
            handler() {
                this.displayInDashboard = this.graphic?.displayInDashboard;
            },
            deep: true,
        },
        seriesData(newVal) {
            this.mutatedSeriesData = newVal;
        },
    },
    created() {
        this.displayInDashboard = this.graphic.displayInDashboard;
    },
    methods: {
        async addSeries() {
            this.showSpinnerOnUpdate = true;
            const { siteId } = this.$route.params;
            await this.$rq.addSerie(siteId, this.graphicId, {});
            this.addSeriesDisabled = true;
            this.$toasted.global.toast_success({
                message: this.$t('graphics.series.msgAddSuccess'),
            });
            await this.fetchSeries();
            this.showSpinnerOnUpdate = false;
        },
        async updateOneSeries(updatedSeries) {
            try {
                if (updatedSeries) {
                    this.showSpinnerOnUpdate = true;
                    const { siteId } = this.$route.params;
                    const payload = {
                        ...updatedSeries,
                        filter: [updatedSeries.filter],
                    };
                    await this.$rq.updateSerie(siteId, this.graphicId, updatedSeries.id, payload);
                    this.addSeriesDisabled = false;
                    await this.getOneSeriesData(updatedSeries.id);
                    await this.fetchSeries();
                    this.getSeriesData();
                    this.showSpinnerOnUpdate = false;
                    this.$toasted.global.toast_success({
                        message: this.$t('graphics.series.msgUpdateSuccess'),
                    });
                } else {
                    const { siteId } = this.$route.params;
                    this.showSpinnerOnUpdate = true;
                    await Promise.all(
                        this.series.map(({ id, ...serie }) => {
                            const payload = {
                                ...serie,
                                filter: [serie.filter],
                            };
                            return this.$rq.updateSerie(siteId, this.graphicId, id, payload);
                        }),
                    );
                    this.addSeriesDisabled = false;
                    this.getSeriesData();
                    this.showSpinnerOnUpdate = false;
                    this.$toasted.global.toast_success({
                        message: this.$t('graphics.series.msgUpdateSuccess'),
                    });
                }
            } catch (err) {
                return err;
            }
            return null;
        },
        fetchSeries() {
            this.$emit('fetchSeries', this.graphic.id);
        },
        updateGraphicDateRange(graphicId, graphic) {
            const { siteId } = this.$route.params;
            this.$store.dispatch('editGraphic', { siteId, graphicId, graphic });
            this.$emit('reload');
        },
        onSettingChange(key) {
            const payload = {
                [key]: this[key],
            };
            if (key === 'recurrency' && (this.dateFrom?.length || this.dateTo?.length)) {
                payload.dateTo = null;
                payload.dateFrom = null;
            }
            this.updateGraphicDateRange(this.graphicId, payload);
        },
        async getOneSeriesData(seriesId) {
            const { siteId } = this.$route.params;
            try {
                const { series } = await this.$rq.getSerieData(siteId, this.graphicId, seriesId, {});
                const seriesBasedOnGraphic = this.getSeries(this.graphicId);
                const componentNameArr = series.map(graphRef => {
                    const graphicRef = seriesBasedOnGraphic.find(serieRef => serieRef.component.id === graphRef.component);
                    return graphicRef.component.name;
                });

                const newSeries = this.formattingSeries(series, componentNameArr);
                // Check if any newSeries already exists in mutatedSeriesData by id
                // Replace newSeries items with existing ones from mutatedSeriesData if found
                const updatedNewSeries = this.mutatedSeriesData.map(ns => {
                    const existing = this.mutatedSeriesData.find(msd => msd.id === newSeries.id);
                    if (existing) {
                        return newSeries;
                    }
                    return ns;
                });

                this.mutatedSeriesData = updatedNewSeries;
            } catch (err) {
                return err;
            }
            return null;
        },
        getSeriesData(graphicId = this.graphicId) {
            this.$emit('getSeriesData', graphicId);
        },
        updatedOpenedGraphs(data) {
            this.$emit('updatedOpenedGraphs', data);
        },
        formattingSeries(series, componentNameRef) {
            return series.map(({ analysisType: { name }, data, id, productMeter, flowMeter }, index) => ({
                id,
                name: `${name[this.lang]}-${componentNameRef[index]}`,
                data: (() => {
                    if (!data.length) return [];
                    if (productMeter) {
                        return this.formatProductLevelData(data);
                    }
                    if (flowMeter) {
                        return this.formatFlowMeter(data);
                    }
                    return data.map(d => ({
                        ...d,
                        date: new Date(d.measuredAt),
                        value: +d.value,
                    }));
                })(),
            }));
        },
    },
};
</script>
